import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  PathologyBackButton,
  PathologySubpageShell,
  PATHOLOGY_PAGE_TITLE_SX,
} from "../components/Pathology/pathologyPageChrome";
import { getDynamicTemplateApi } from "../services/dynamic-template.service";
import { useGetApi } from "../hooks/useGetApi";
import { toast } from "react-toastify";
import {
  getPathologyByIdApi,
  updatePathologyApi,
} from "../services/pathology.service";
import { getUserApi } from "../services/user.service";
import Loader from "../components/Loader/loader";

function rangeLabelFromRow(row) {
  const r = row?.fields?.[0]?.settings?.ranges?.[0];
  if (!r || r === "-") return "-";
  if (r.operation === "Between")
    return `${r.value1} - ${r.value2}`;
  if (r.operation === "Greater Than") return `> ${r.value1}`;
  if (r.operation === "Less Than") return `< ${r.value1}`;
  return "-";
}

/** Prefer higher priority when multiple templates match the same item. */
function pickPathologyTemplate(templates) {
  if (!Array.isArray(templates) || templates.length === 0) return null;
  if (templates.length === 1) return templates[0];
  return [...templates].sort(
    (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
  )[0];
}

function buildBlankFormRows(template) {
  const rows = template?.dynamicForm;
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    id: row._id,
    label: row.label || row?.fields?.[0]?.displayText || "",
    value: "",
    range: rangeLabelFromRow(row),
    unit: row.unit || row?.fields?.[0]?.unit || "",
    isRequired: row.isRequired ?? false,
  }));
}

function mergeTemplateWithSavedResults(template, savedResults) {
  const rows = template?.dynamicForm;
  if (!Array.isArray(rows) || !Array.isArray(savedResults)) return [];
  return rows.map((row, idx) => {
    const displayLabel = row.label || row?.fields?.[0]?.displayText || "";
    const savedRow =
      savedResults.find(
        (s) =>
          s.label &&
          displayLabel &&
          String(s.label).trim() === String(displayLabel).trim()
      ) ||
      savedResults[idx] ||
      {};
    const computedRange = rangeLabelFromRow(row);
    return {
      id: row._id,
      label: displayLabel,
      value:
        savedRow.value != null && String(savedRow.value) !== ""
          ? String(savedRow.value)
          : "",
      range:
        savedRow.range != null &&
        savedRow.range !== "" &&
        savedRow.range !== "-"
          ? savedRow.range
          : computedRange,
      unit: savedRow.unit || row?.fields?.[0]?.unit || row.unit || "",
      isRequired: Boolean(savedRow.isRequired),
    };
  });
}

export default function PathologyAddReport() {
  const navigate = useNavigate();
  const { id: itemIdFromRoute } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const requestId =
    searchParams.get("requestId") || location.state?.ID || null;
  const allDetailsFallback = location.state?.allData;

  const [nextLoading, setNextLoading] = useState(false);
  const [formData, setFormData] = useState([]);
  const [fetchedBy, setFetchedBy] = useState("");
  const inProgressInitKeyRef = useRef("");

  const { dataList: pathologyRaw, isLoading: pathologyLoading } = useGetApi({
    apiFunction: getPathologyByIdApi,
    params: { id: requestId },
    dependencies: [requestId],
    skip: !requestId,
    rawData: true,
  });

  const { dataList: templateList } = useGetApi({
    apiFunction: getDynamicTemplateApi,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      item: itemIdFromRoute,
      formType: "Pathology",
    },
    dependencies: [itemIdFromRoute],
    skip: !itemIdFromRoute,
    dataKey: "templates",
  });

  const pathologyDoc = useMemo(() => {
    if (
      pathologyRaw &&
      typeof pathologyRaw === "object" &&
      !Array.isArray(pathologyRaw)
    ) {
      return pathologyRaw;
    }
    return null;
  }, [pathologyRaw]);

  const primaryTemplate = useMemo(
    () => pickPathologyTemplate(templateList),
    [templateList]
  );

  const displayDetails = pathologyDoc || allDetailsFallback;

  const {
    dataList: fetchedByUsers,
    isLoading: fetchedByUsersLoading,
  } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000, page: 1 },
    dependencies: [],
    dataKey: "users",
  });

  const fetchedByOptions = useMemo(() => {
    if (!Array.isArray(fetchedByUsers)) return [];
    return fetchedByUsers.map((u) => ({
      value: u._id,
      label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.email || "Unknown User",
    }));
  }, [fetchedByUsers]);

  /** Supports refresh: ?requestId=… when router state is lost */
  const workflowStatus = useMemo(() => {
    const fromState = location.state?.status;
    if (fromState === "In Progress" || fromState === "Dr Approval") {
      return fromState;
    }
    const rec = pathologyDoc?.status;
    if (rec === "In Progress" || rec === "Dr Approval") {
      return rec;
    }
    return null;
  }, [location.state?.status, pathologyDoc?.status]);

  useEffect(() => {
    const raw = pathologyDoc?.report?.fetchedBy;
    if (!raw) {
      setFetchedBy("");
      return;
    }
    if (typeof raw === "string") {
      setFetchedBy(raw);
      return;
    }
    if (typeof raw === "object" && raw?._id) {
      setFetchedBy(raw._id);
      return;
    }
    setFetchedBy("");
  }, [pathologyDoc?.report?.fetchedBy]);

  useEffect(() => {
    if (!primaryTemplate?.dynamicForm?.length) {
      setFormData([]);
      return;
    }

    const doc = pathologyDoc || allDetailsFallback;
    const saved = doc?.report?.results;

    if (workflowStatus === "Dr Approval") {
      if (Array.isArray(saved) && saved.length > 0) {
        setFormData(
          mergeTemplateWithSavedResults(primaryTemplate, saved)
        );
      } else {
        setFormData(buildBlankFormRows(primaryTemplate));
      }
      return;
    }

    if (workflowStatus === "In Progress") {
      const key = `${primaryTemplate._id}|In Progress`;
      if (inProgressInitKeyRef.current !== key) {
        inProgressInitKeyRef.current = key;
        setFormData(buildBlankFormRows(primaryTemplate));
      }
      return;
    }

    inProgressInitKeyRef.current = "";
  }, [
    primaryTemplate,
    workflowStatus,
    pathologyDoc,
    allDetailsFallback,
  ]);

  const templateRows = primaryTemplate?.dynamicForm || [];

  const handleInputChange = (rowId, value) => {
    setFormData((prevData) =>
      prevData.map((item) =>
        item.id === rowId ? { ...item, value } : item
      )
    );
  };

  const handleCheckboxChange = (rowId) => {
    setFormData((prevData) =>
      prevData.map((item) =>
        item.id === rowId ? { ...item, isRequired: !item.isRequired } : item
      )
    );
  };

  const submitHandler = async () => {
    if (!requestId) {
      toast.error(
        "Missing pathology request id. Open add report from pathology progress or use a link with ?requestId=."
      );
      return;
    }
    if (!primaryTemplate?._id || !templateRows.length) {
      toast.error("No pathology template found for this test.");
      return;
    }

    /** When true, the "Not Required" box is checked — row may be left blank. */
    const rowIsOptional = (item) => Boolean(item.isRequired);

    let isValid = true;
    formData.forEach((item) => {
      const row = templateRows.find((r) => r._id === item.id);
      if (row?.fields?.[0]?.type === "Numeric") {
        const range = row?.fields?.[0]?.settings?.ranges?.[0];
        if (range === "-" || !range?.operation) return;

        if (rowIsOptional(item)) {
          const raw = item.value;
          if (raw === "" || raw === null || raw === undefined) return;
        }

        const numericValue = parseFloat(item.value);
        let errorMessage = "";
        if (Number.isNaN(numericValue)) {
          errorMessage = `${row?.fields?.[0]?.displayText}: Enter a valid number`;
        } else if (range.operation === "Between") {
          if (numericValue < range.value1 || numericValue > range.value2) {
            errorMessage = `${row?.fields?.[0]?.displayText}: Value must be between ${range.value1} and ${range.value2}`;
          }
        } else if (range.operation === "Greater Than") {
          if (numericValue <= range.value1) {
            errorMessage = `${row?.fields?.[0]?.displayText}: Value must be greater than ${range.value1}`;
          }
        } else if (range.operation === "Less Than") {
          if (numericValue >= range.value1) {
            errorMessage = `${row?.fields?.[0]?.displayText}: Value must be less than ${range.value1}`;
          }
        }
        if (errorMessage) {
          toast.error(errorMessage);
          isValid = false;
        }
      }
    });

    if (!isValid) return;

    const missing = formData.some((item) => {
      if (rowIsOptional(item)) return false;
      return (
        item.value === "" ||
        item.value === null ||
        item.value === undefined
      );
    });
    if (missing) {
      toast.error("Fill all required fields, or mark unused rows as Not Required.");
      return;
    }

    const payload = {
      status: workflowStatus === "Dr Approval" ? "Dispatched" : "Dr Approval",
      report: {
        template: primaryTemplate._id,
        fetchedBy: fetchedBy || undefined,
        results: formData.map(({ id: _rowId, ...rest }) => rest),
      },
    };

    setNextLoading(true);
    const response = await updatePathologyApi(requestId, payload);
    setNextLoading(false);

    const ok =
      response?.code === 200 ||
      response?.code === 201 ||
      response?.isSuccess;
    if (ok) {
      toast.success("Pathology updated successfully");
      navigate(-1);
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const isApprovalReadOnly = workflowStatus === "Dr Approval";

  if (!itemIdFromRoute) {
    return (
      <Container maxWidth={false} disableGutters>
        <Header />
        <Box sx={{ display: "flex" }}>
          <Sidebar />
          <PathologySubpageShell>
            <PathologyBackButton onClick={() => navigate("/pathology")} />
            <Typography sx={{ mb: 1 }}>Invalid route: missing item id.</Typography>
          </PathologySubpageShell>
        </Box>
      </Container>
    );
  }

  if (!requestId) {
    return (
      <Container maxWidth={false} disableGutters>
        <Header />
        <Box sx={{ display: "flex" }}>
          <Sidebar />
          <PathologySubpageShell>
            <PathologyBackButton onClick={() => navigate("/pathology")} />
            <Typography sx={{ mb: 1 }}>
              Missing pathology request. Use a URL with{" "}
              <code>?requestId=...</code> or open this page from the pathology
              progress screen.
            </Typography>
          </PathologySubpageShell>
        </Box>
      </Container>
    );
  }

  if (requestId && !location.state?.status && pathologyLoading) {
    return (
      <Container maxWidth={false} disableGutters>
        <Header />
        <Box sx={{ display: "flex" }}>
          <Sidebar />
          <PathologySubpageShell>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <Loader />
            </Box>
          </PathologySubpageShell>
        </Box>
      </Container>
    );
  }

  if (workflowStatus !== "In Progress" && workflowStatus !== "Dr Approval") {
    return (
      <Container maxWidth={false} disableGutters>
        <Header />
        <Box sx={{ display: "flex" }}>
          <Sidebar />
          <PathologySubpageShell>
            <PathologyBackButton onClick={() => navigate(-1)} />
            <Typography color="error" sx={{ mb: 1 }}>
              Invalid step. This request must be In Progress or Dr Approval
              (current: {pathologyDoc?.status || "unknown"}).
            </Typography>
          </PathologySubpageShell>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Sidebar />
        <PathologySubpageShell>
          <PathologyBackButton onClick={() => navigate(-1)} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0,
              mb: "20px",
              color: "#5D6A82",
            }}
          >
            <Typography sx={PATHOLOGY_PAGE_TITLE_SX}>
              Add Result Details
            </Typography>
            <Tooltip title="Close">
              <IconButton onClick={() => navigate(-1)} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              marginBottom: "20px",
              backgroundColor: "#F0F7FC",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              paddingInline: "15px",
            }}
          >
            <Typography sx={{ fontSize: 14, paddingLeft: 2, paddingTop: 2, paddingBottom: 2 }}>
              Basic Information
            </Typography>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
                paddingBottom: "20px",
              }}
              container
              spacing={2}
            >
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Sample ID"
                  value={displayDetails?.sample?.sampleId || ""}
                  slotProps={{ input: { readOnly: true } }}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="pathology-machine-label">Machine Name</InputLabel>
                  <Select
                    sx={{ backgroundColor: "white" }}
                    labelId="pathology-machine-label"
                    id="pathology-machine"
                    label="Machine Name"
                    input={<OutlinedInput label="Machine Name" />}
                    value=""
                    displayEmpty
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="pathology-fetched-label">Fetched By</InputLabel>
                  <Select
                    sx={{ backgroundColor: "white" }}
                    labelId="pathology-fetched-label"
                    id="pathology-fetched"
                    label="Fetched By"
                    input={<OutlinedInput label="Fetched By" />}
                    value={fetchedBy}
                    displayEmpty
                    disabled={
                      workflowStatus !== "In Progress" &&
                      workflowStatus !== "Dr Approval"
                    }
                    onChange={(e) => setFetchedBy(e.target.value)}
                  >
                    <MenuItem value="">
                      {fetchedByUsersLoading ? "Loading..." : "Select"}
                    </MenuItem>
                    {fetchedByOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: -1.5,
              marginBottom: 2,
            }}
          >
            <Checkbox disabled checked />
            <Typography>Select All Report</Typography>
          </Box>

          <Box
            sx={{
              marginBottom: "20px",
              backgroundColor: "#F0F7FC",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              paddingInline: "15px",
            }}
          >
            <Typography
              variant="subtitle1"
              color="#5D6A82"
              paddingTop={2}
              paddingBottom={2}
            >
              {primaryTemplate?.name || "—"}
            </Typography>
            {!templateRows.length ? (
              <Typography sx={{ pb: 2 }} color="text.secondary">
                No template fields for this item. Link a Pathology dynamic
                template in Master.
              </Typography>
            ) : null}
            {templateRows.map((row) => {
              const currentItem =
                formData.find((item) => item.id === row._id) || {};
              return (
                <Grid
                  key={row?._id}
                  container
                  spacing={3}
                  sx={{ paddingLeft: 2, paddingBottom: 2 }}
                >
                  <Grid item xs={4}>
                    {row?.fields?.[0]?.type === "Numeric" || isApprovalReadOnly ? (
                      <TextField
                        fullWidth
                        label={row.label || row?.fields?.[0]?.displayText}
                        sx={{ backgroundColor: "white" }}
                        value={currentItem.value ?? ""}
                        disabled={isApprovalReadOnly}
                        onChange={(e) =>
                          handleInputChange(row._id, e.target.value)
                        }
                      />
                    ) : (
                      <FormControl variant="standard" fullWidth>
                        <InputLabel id={`ph-sel-${row._id}`}>
                          {row?.fields?.[0]?.displayText}
                        </InputLabel>
                        <Select
                          labelId={`ph-sel-${row._id}`}
                          id={`ph-sel-ctl-${row._id}`}
                          value={currentItem.value || ""}
                          disabled={isApprovalReadOnly}
                          onChange={(e) =>
                            handleInputChange(row._id, e.target.value)
                          }
                          label={row?.fields?.[0]?.displayText}
                        >
                          {row?.fields?.[0]?.settings?.shortcuts?.map(
                            (shortcut, index) => (
                              <MenuItem key={index} value={shortcut?.shortForm}>
                                {shortcut?.shortForm}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Typography>{currentItem.unit}</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Typography>{currentItem.range || "N/A"}</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Checkbox
                      checked={currentItem.isRequired || false}
                      disabled={isApprovalReadOnly}
                      onChange={() => handleCheckboxChange(row._id)}
                    />
                    <Typography>Not Required</Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 6,
              gap: 2,
            }}
          >
            {workflowStatus === "Dr Approval" && (
              <Button variant="outlined" sx={{ width: "200px" }} disabled>
                Print Preview
              </Button>
            )}
            <Button
              variant="contained"
              sx={{ width: "200px" }}
              onClick={submitHandler}
              disabled={nextLoading || !templateRows.length}
            >
              {nextLoading ? <CircularProgress size="20px" /> : "Save"}
            </Button>
          </Box>
        </PathologySubpageShell>
      </Box>
    </Container>
  );
}
